import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { shareItemWithFamily } from '../../api/client';
import { base64UrlToBytes } from '../../crypto/base64url';
import { prepareShareForFamily } from '../../components/prepareShareForFamily';
import { familyKeyAuthentic } from '../../pkastore/familyKeyAuth';
import { HbsInput } from '../../components/hbs/Input';
import { HbsButton } from '../../components/hbs/Button';
import { buildRrule, parseRrule, type Frequency } from '../../binder/recurrence';

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const INPUT_CLASS =
  'w-full px-3 py-2 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus:border-hbs-primary focus:ring-4 focus:ring-hbs-primary/20';

/**
 * Create/edit a family schedule (a recurring reminder, e.g. "Change the furnace
 * filter" monthly). Family-first, like documents: written directly as a shared
 * `ORG#` item via the `share-item-v1` ECIES path (create) or re-encrypted in
 * place via `updateItem` (edit) — no special crypto.
 *
 * The recurrence is authored through a simple frequency + interval picker that
 * generates a constrained `FREQ=…;INTERVAL=n` rrule. A schedule that already
 * carries a custom rrule (authored elsewhere) is preserved as-is unless the user
 * picks a new frequency. `lastCompletedAt` (mark-done state) is round-tripped.
 */
export default function ScheduleEditorPage(): JSX.Element {
  const { orgId, categoryId, itemId } = useParams<{ orgId: string; categoryId: string; itemId?: string }>();
  const navigate = useNavigate();
  const { orgs, sync, updateItem, items } = useSafe();
  const categoriesByOrg = useFamilyCategories();

  const org = orgs.find((o) => o.id === orgId);
  const category = (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === categoryId);

  const existing = itemId
    ? items.find((i) => i.id === itemId && i.orgId === orgId && i.type === 'schedule')
    : undefined;
  const isEdit = !!existing;
  const parsed = parseRrule(existing?.rrule);

  const [title, setTitle] = useState(existing?.name ?? '');
  // 'custom' is only reachable when editing a schedule whose rrule is outside
  // the picker's subset; selecting a real frequency replaces it.
  const [frequency, setFrequency] = useState<Frequency | 'custom'>(parsed.custom ? 'custom' : parsed.frequency);
  const [interval, setIntervalValue] = useState(parsed.interval);
  const [dtstart, setDtstart] = useState(existing?.dtstart ?? new Date().toISOString().slice(0, 10));
  const [relatedItemId, setRelatedItemId] = useState(existing?.relatedItemId ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const backTo = `/orgs/${orgId}/binder/${categoryId}`;

  // Single-link targets: org-scoped contacts + documents (a schedule is "about"
  // one of them), excluding this schedule itself.
  const linkTargets = items.filter(
    (i) => i.orgId === orgId && i.id !== itemId && (i.type === 'contact' || i.type === 'document'),
  );

  const showInterval = frequency !== 'none' && frequency !== 'custom';

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!title.trim() || !orgId || !categoryId) return;
    if (!org?.publicKey) {
      setError('Family is missing its public key. Try refreshing.');
      return;
    }
    // PKA (A2): authenticate the family public key before wrapping a new item to it.
    if (!familyKeyAuthentic(org)) {
      setError("Could not verify this family's security key — do not save. Ask a family admin to sign in to the latest version.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rrule = frequency === 'custom' ? existing?.rrule : buildRrule(frequency, interval);
      const fields = {
        name: title.trim(),
        rrule,
        dtstart: dtstart || undefined,
        relatedItemId: relatedItemId || undefined,
        // Preserve mark-done state across edits (no UI to change it here).
        lastCompletedAt: existing?.lastCompletedAt,
      };

      if (isEdit && existing) {
        await updateItem(existing.id, { type: 'schedule', ...fields });
        await sync();
        navigate(`/orgs/${orgId}/binder/${categoryId}/schedules/${existing.id}`);
        return;
      }

      const schedule: VaultItem = {
        id: '', type: 'schedule', ...fields, categoryId,
        sensitive: false, favorite: false, tags: [],
        createdAt: '', updatedAt: '',
      };
      const encrypted = await prepareShareForFamily(schedule, base64UrlToBytes(org.publicKey), null, orgId);
      const itemToShare = category?.circleId ? { ...encrypted, circleId: category.circleId } : encrypted;
      const result = await shareItemWithFamily(itemToShare);
      if (!result.ok) {
        setError('Could not save the schedule. Please try again.');
        return;
      }
      await sync();
      navigate(backTo);
    } catch (err) {
      console.error('ScheduleEditorPage: save failed', err);
      setError('Could not save the schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="schedule-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <div className="mb-2">
          <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
            {org?.name ?? 'Family'}{category ? ` · ${category.name}` : ''}
          </p>
          <h1 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text">
            {isEdit ? 'Edit schedule' : 'New schedule'}
          </h1>
        </div>

        <HbsInput label="Name" value={title} onChange={setTitle} placeholder="e.g. Change the furnace filter" required autoFocus name="title" />

        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="sched-freq" className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Repeats
            </label>
            <select
              id="sched-freq"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency | 'custom')}
              className={INPUT_CLASS}
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              {parsed.custom && <option value="custom">Custom: {parsed.raw}</option>}
            </select>
          </div>

          {showInterval && (
            <div className="w-28">
              <label htmlFor="sched-interval" className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
                Every
              </label>
              <input
                id="sched-interval"
                type="number"
                min={1}
                value={interval}
                onChange={(e) => setIntervalValue(Math.max(1, Number(e.target.value) || 1))}
                className={INPUT_CLASS}
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="sched-dtstart" className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
            Start date
          </label>
          <input
            id="sched-dtstart"
            type="date"
            value={dtstart.slice(0, 10)}
            onChange={(e) => setDtstart(e.target.value)}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label htmlFor="sched-related" className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
            Related item
          </label>
          <select
            id="sched-related"
            value={relatedItemId}
            onChange={(e) => setRelatedItemId(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">None</option>
            {linkTargets.map((i) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </div>

        {error && <p role="alert" className="text-[12px] text-hbs-warning">{error}</p>}
      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="schedule-save-button" type="submit" form="schedule-editor-form" loading={loading} fullWidth>Save</HbsButton>
        <HbsButton id="schedule-cancel-button" type="button" variant="ghost" fullWidth onClick={() => navigate(backTo)}>Cancel</HbsButton>
      </div>
    </div>
  );
}
